/*
 * Copyright (c) 2025. Haodong JU
 */

import { hillo } from 'hillo'
import { cloudV2Url } from '@/model/dataLayer/api/SaaS/SaaSUtils'
import GlobalConfig from '@/model/global/GlobalSettings'
import IKDataEntity from '@/plugins/EntityFrameWork/IKDataEntity'

const path = 'api/membership/labels'

const entity = {
  operations: {
    displayName: 'operation',
    type: IKDataEntity.Types.Integer,
    form: false,
    tableConfig: {
      overwrite: true
    }
  },
  name: {
    displayName: '标签名称',
    type: IKDataEntity.Types.String,
    required: true
  },
  description: {
    displayName: '描述',
    type: IKDataEntity.Types.String,
    required: false
  },
  color: {
    displayName: '颜色',
    type: IKDataEntity.Types.String,
    required: true
  },
  createTimestamp: {
    displayName: '创建时间',
    type: IKDataEntity.Types.Date,
    form: false
  }
}

const config = {
  load: async function (filter) {
    return (await hillo.get(cloudV2Url + path + '/business-layer/' + GlobalConfig.blId))?.labels || []
  },
  add: async function (item) {
    item.blId = GlobalConfig.blId
    return (await hillo.jsonPost(cloudV2Url + path, {
      ...item
    }))
  },
  edit: async function (item) {
    return (await hillo.jsonPost(cloudV2Url + path + '/' + item.id, {
      name: item.name,
      description: item.description,
      color: item.color
    }))
  },
  delete: async function (item) {
    return (await hillo.post(cloudV2Url + path + '/' + item.id + '/delete'))
  }
}

export default IKDataEntity.ModelFactory(entity, config)

/**
 * Get a membership label by ID
 *
 * @param {number} id - The label ID
 * @returns {Promise<Object>} - Promise that resolves to the label details
 */
export async function getLabelById (id) {
  return await hillo.get(cloudV2Url + path + '/' + id)
}

/**
 * Get all labels for a business layer
 *
 * @param {number} blId - The business layer ID (defaults to current business layer)
 * @returns {Promise<Object>} - Promise that resolves to an object containing labels array
 */
export async function getLabelsForBusinessLayer (blId = GlobalConfig.blId) {
  return await hillo.get(cloudV2Url + path + '/business-layer/' + blId)
}

/**
 * Add labels to a membership
 *
 * @param {number} userMembershipId - The user membership ID
 * @param {Array<number>} labelIds - Array of label IDs to add
 * @returns {Promise<Object>} - Promise that resolves to an object containing the updated labels
 */
export async function addLabelsToMembership (userMembershipId, labelIds) {
  return await hillo.jsonPost(cloudV2Url + path + '/add-to-membership', {
    userMembershipId,
    labelIds
  })
}

/**
 * Set labels for a membership (replaces all existing labels)
 *
 * @param {number} userMembershipId - The user membership ID
 * @param {Array<number>} labelIds - Array of label IDs to set
 * @returns {Promise<Object>} - Promise that resolves to an object containing the updated labels
 */
export async function setMembershipLabels (userMembershipId, labelIds) {
  return await hillo.jsonPost(cloudV2Url + path + '/set-membership-labels', {
    userMembershipId,
    labelIds
  })
}

/**
 * Remove labels from a membership
 *
 * @param {number} userMembershipId - The user membership ID
 * @param {Array<number>} labelIds - Array of label IDs to remove
 * @returns {Promise<void>} - Promise that resolves when labels are removed
 */
export async function removeLabelsFromMembership (userMembershipId, labelIds) {
  return await hillo.jsonPost(cloudV2Url + path + '/remove-from-membership', {
    userMembershipId,
    labelIds
  })
}

/**
 * Get all labels for a membership
 *
 * @param {number} userMembershipId - The user membership ID
 * @returns {Promise<Object>} - Promise that resolves to an object containing labels array
 */
export async function getLabelsForMembership (userMembershipId) {
  return await hillo.get(cloudV2Url + path + '/membership/' + userMembershipId)
}
